import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, parseApiError } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { Card } from '../components/Card';
import { ClanApplicationsPanel } from '../components/ClanApplicationsPanel';
import { ClanBrandingHero } from '../components/ClanBrandingHero';
import { CaptainsPanel } from '../components/CaptainsPanel';
import { ClanTeamsBoard } from '../components/ClanTeamsBoard';
import { ClanMemberRow, ToastHost } from '../components/ClanMemberRow';
import { GhostSeatManager } from '../components/GhostSeatManager';
import { HelperCard } from '../components/HelperCard';
import { StatusBadge } from '../components/StatusBadge';
import { PageSpinner } from '../components/Spinner';
import { PageWrapper } from '../components/PageWrapper';
import { ClanSeatPicker, toggleSeatSelection, resolveTeamSizeCeiling } from '../components/MatchRosterInitPanel';
import { canWrite, canRegisterClan } from '../lib/roles';
import { canManageTeamScoped } from '../lib/captainAccess';
import { useEffectiveMe, useEffectiveRole, useViewAs } from '../lib/viewAs';
import { Users, Crown, Gamepad2, Swords, CheckCircle2, Loader2, Search, Trophy } from 'lucide-react';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import type { DiscoverableClanSummary, Me } from '../lib/api';

// Captain-delegation UI flag — ships DARK. The Make-Captain controls + Captains
// card stay hidden until VITE_DELEGATION_UI=1 at build time. Pair with the backend
// DELEGATION_OVERLAY_ENABLED=1 to fully activate the feature.
const DELEGATION_UI_ENABLED = import.meta.env.VITE_DELEGATION_UI === '1';

// ── Schedule Match Form ───────────────────────────────────────────────────────

function ScheduleMatchForm({ clanName }: { clanName: string }) {
  const [compId, setCompId] = useState('');
  const [opponent, setOpponent] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  // T7 (2026-07-24 — closes the Route-2 leak): the proposer's OWN squad,
  // ticked in the SAME step as date/time (Lisa's bundled flow — "Cap 1 sets
  // date + time AND ticks their squad, in one go"). Nobody starts ticked —
  // same no-auto-fill rule MatchRosterInitPanel/CardAgreementPanel already
  // enforce. Reset whenever the competition changes: a different comp can
  // carry a different teamSize ceiling.
  const [fieldedSquad, setFieldedSquad] = useState<string[]>([]);

  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  const { data: enrolledClans } = useQuery({
    queryKey: ['competitionClans', compId],
    queryFn: () => api.competitionClans(compId),
    enabled: !!compId,
  });

  // My OWN clan's seats — the picker below fields FROM here, exactly as
  // ClanSeatPicker expects (a ClanDetail carrying ClanSeat[]).
  const myClanQuery = useQuery({ queryKey: ['clan', clanName], queryFn: () => api.clan(clanName) });
  const selectedComp = (comps || []).find(c => c.id === compId);
  const teamSize = resolveTeamSizeCeiling(selectedComp?.teamSize);

  const opponents = (enrolledClans || []).filter(c => c.name !== clanName);

  const mutation = useMutation({
    mutationFn: api.startMatch,
    onSuccess: result => {
      const dmNote = result.dmsSent
        ? 'Match Cards sent to both leaders via Discord DM.'
        : 'Match created, but one or more DMs failed — check Discord.';
      setSuccess(`Match scheduled: ${result.clan1} vs ${result.clan2}. ${dmNote}`);
      setCompId('');
      setOpponent('');
      setFieldedSquad([]);
      setError('');
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const liveComps = (comps || []).filter(c => c.status !== 'CLOSED');
  const squadFloorMet = fieldedSquad.length >= 1;

  return (
    <Card className="mb-6">
      <h2 className="mb-4 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
        <Swords className="h-4 w-4 text-forge-400" /> Schedule a Match
      </h2>

      {success ? (
        <div className="flex items-start gap-3 rounded-lg bg-white/[0.04] px-4 py-3">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
          <div>
            <p className="text-sm lg:text-base text-ink-body">{success}</p>
            <button
              onClick={() => setSuccess('')}
              className="mt-2 text-xs lg:text-sm text-ink-meta hover:text-ink-secondary"
            >
              Schedule another
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="label">Competition</label>
            <select
              value={compId}
              onChange={e => { setCompId(e.target.value); setOpponent(''); setFieldedSquad([]); setError(''); }}
              className="input w-full"
            >
              <option value="">Select competition…</option>
              {liveComps.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id.replace('COMP#', '')} — {c.status}
                </option>
              ))}
            </select>
          </div>

          {compId && (
            <div>
              <label className="label">Opponent Clan</label>
              {opponents.length === 0 ? (
                <p className="text-xs lg:text-sm text-ink-meta">
                  No other clans enrolled in this competition yet.
                </p>
              ) : (
                <select
                  value={opponent}
                  onChange={e => { setOpponent(e.target.value); setError(''); }}
                  className="input w-full"
                >
                  <option value="">Select opponent…</option>
                  {opponents.map(c => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* T7 (2026-07-24) — who's fielding for US, ticked right here so the
              same click that sends the request also carries the squad; the
              opponent captain ticks their own when they agree (T1b,
              CardAgreementPanel), unchanged. */}
          {compId && (
            <div>
              <label className="label">Who&apos;s fielding for {clanName}?</label>
              <ClanSeatPicker
                clanLabel={clanName}
                detail={myClanQuery.data}
                isLoading={myClanQuery.isLoading}
                error={myClanQuery.error}
                teamSize={teamSize}
                maxSquad={teamSize}
                selected={fieldedSquad}
                onToggle={id => setFieldedSquad(prev => toggleSeatSelection(prev, id, teamSize))}
              />
              {!squadFloorMet && (
                <p className="mt-1 text-xs lg:text-sm text-ink-meta">
                  Tick at least one player — only ticked players get match-card slots.
                </p>
              )}
            </div>
          )}

          {error && <p className="text-xs lg:text-sm text-red-400">{error}</p>}

          <button
            onClick={() => {
              // Match-team-autofill (Lisa, 2026-07-13): if the chosen
              // opponent is a specific entered TEAM (not a legacy whole-clan
              // row), thread its clanId/teamNo through as an explicit
              // pointer — see StartMatchInput's doc comment. Never derived
              // by parsing `opponent` (the display name) apart.
              const chosen = opponents.find(o => o.name === opponent);
              mutation.mutate({
                compId, opponentClan: opponent, fieldedSquad,
                ...(chosen?.teamNo != null ? { opponentClanId: chosen.clanId, opponentTeamNo: chosen.teamNo } : {}),
              });
            }}
            disabled={!compId || !opponent || !squadFloorMet || mutation.isPending}
            className={`btn-primary ${MORPH} w-full`}
          >
            {mutation.isPending ? 'Scheduling…' : 'Schedule Match — Bot DMs both leaders'}
          </button>

          <p className="text-xs lg:text-sm text-ink-meta">
            Both clan leaders will receive a Match Card in Discord DM with Confirm / Dispute buttons.
          </p>
        </div>
      )}
    </Card>
  );
}

// ── No-clan landing (visible when user has no clan) ─────────────────────────

function DiscoverClansInline() {
  const { data, isLoading } = useQuery({
    queryKey: ['discoverClans'],
    queryFn: () => api.discoverClans(),
  });

  if (isLoading) {
    return (
      <Card>
        <p className="text-xs lg:text-sm text-ink-meta">Loading discoverable clans…</p>
      </Card>
    );
  }

  const clans = data?.clans ?? [];
  if (clans.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-ink-secondary" />
        <h2 className="font-display text-xs font-black uppercase tracking-wider text-ink-meta">Discover clans</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {clans.map((clan: DiscoverableClanSummary) => (
          <Link
            key={clan.clanId}
            to={`/clans/browse?focus=${encodeURIComponent(clan.name)}`}
            className={`${MORPH} block rounded-lg bg-white/[0.04] p-3 transition hover:bg-white/[0.06]`}
          >
            <div className="mb-1 flex items-center gap-2">
              <Crown className="h-3.5 w-3.5 text-ink-secondary" />
              <p className="truncate font-display text-sm lg:text-base font-black uppercase tracking-wide text-ink-primary">
                {clan.name}
              </p>
            </div>
            {clan.description && (
              <p className="line-clamp-2 text-[11px] lg:text-xs text-ink-secondary">{clan.description}</p>
            )}
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-ink-meta">
              <Users className="-mt-0.5 mr-1 inline h-3 w-3" strokeWidth={2.5} />
              {clan.memberCount} members
            </p>
          </Link>
        ))}
      </div>
    </Card>
  );
}

function MyClanIntro({ hasClan, className }: { hasClan: boolean; className?: string }) {
  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });
  const role = useEffectiveRole(me?.role);
  const isFounder = role === 'founder';
  const isLeader = role === 'leader';

  const intro = !hasClan
    ? (isFounder
        ? "Founder view: you can spin up clans for testing. In live use, clans get created by comp admins or by users applying through this surface."
        : "Clans are how you and your teammates show up to a match together. Start your own and invite mates, or apply to join one of the discoverable clans below.")
    : (isFounder
        ? "You run this clan — invite mates, build teams, run your matches."
        : isLeader
        ? "You run this clan. Invite members, approve seat claims, schedule matches, and handle vetoes as they come in."
        : "You’re a member here. Match cards arrive via DM with claim buttons; submissions go through your slot on the card.");

  const points = !hasClan
    ? [
        { text: 'Starting a clan makes you the clan owner; you can rename, invite and disband at will.' },
        { text: 'Applying to an existing clan goes through the leader’s approval — they get a DM with claim buttons.' },
      ]
    : isLeader || isFounder
    ? [
        { text: 'You can only invite people from your Discord server — the one the bot is in.' },
        { text: 'They get a DM with an Accept button — one tap and they’re on the roster.' },
        { text: 'Match cards arrive by DM; veto & scores happen there.' },
      ]
    : [
        { text: 'Match assignments arrive in Discord with a claim button.' },
        { text: 'Submit your screenshot on the match card; if you miss it, your leader can proxy-upload for you.' },
      ];

  return <HelperCard id={hasClan ? 'myclan-intro-has' : 'myclan-intro-empty'} title="What lives here" intro={intro} points={points} dismissible={false} className={className} />;
}

function NoClanLanding() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  // W3 soft-stamp: bind the self-serve clan create to the active workspace org.
  const { activeOrgId } = useWorkspace();

  const { data: myApps } = useQuery({ queryKey: ['myApplications'], queryFn: api.myApplications });
  const applications = myApps?.applications ?? [];

  const createMut = useMutation({
    mutationFn: () => api.createMyClan({ name: name.trim(), description: description.trim() || undefined, orgId: activeOrgId ?? undefined }),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['clan'] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <PageWrapper maxWidth="3xl" spacing="md">
      <div className={`${SETTLE} flex items-center gap-3`} style={settleDelay(0)}>
        <Crown className="h-7 w-7 text-ink-secondary" />
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">My Clan</h1>
          <p className="text-xs lg:text-sm text-ink-meta">Start your own or join an existing one</p>
        </div>
      </div>

      <MyClanIntro hasClan={false} />

      {applications.length > 0 && (
        <Card>
          <h2 className="mb-3 font-display text-[11px] font-black uppercase tracking-[0.22em] text-ink-meta">
            Your Applications
          </h2>
          <div className="space-y-2">
            {applications.map(app => (
              <div key={app.clanId} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate font-display text-sm lg:text-base font-black uppercase tracking-wide text-ink-primary">{app.clanName}</p>
                  <p className="font-mono text-[10px] text-ink-meta">Submitted {new Date(app.createdAt).toLocaleDateString()}</p>
                </div>
                <span className={
                  app.status === 'PENDING'
                    ? 'rounded bg-white/[0.05] px-2 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-amber-300'
                    : app.status === 'ACCEPTED'
                    ? 'rounded bg-white/[0.05] px-2 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-300'
                    : 'rounded bg-white/[0.05] px-2 py-0.5 font-display text-[10px] font-black uppercase tracking-wider text-red-300'
                }>{app.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Start a clan */}
        <Card>
          <h2 className="mb-2 font-display text-sm font-black uppercase tracking-wider text-ink-primary">Start your own clan</h2>
          <p className="mb-4 text-xs lg:text-sm text-ink-secondary">
            You become leader. You can open the clan to public applications later.
          </p>
          <div className="space-y-3">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Clan name"
              className="input w-full"
            />
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder="Description (optional)"
              className="input w-full resize-none"
            />
            {error && <p className="rounded-lg bg-white/[0.04] px-3 py-2 text-xs lg:text-sm text-red-300">{error}</p>}
            <button
              onClick={() => createMut.mutate()}
              disabled={!name.trim() || createMut.isPending}
              className={`btn-primary ${MORPH} flex w-full items-center justify-center gap-1.5 disabled:opacity-50`}
            >
              {createMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crown className="h-3 w-3" />}
              Create Clan
            </button>
          </div>
        </Card>

        {/* Browse clans */}
        <Card>
          <h2 className="mb-2 font-display text-sm font-black uppercase tracking-wider text-ink-primary">Join an existing clan</h2>
          <p className="mb-4 text-xs lg:text-sm text-ink-secondary">
            Browse clans that are accepting new members. Apply with your gamertag and the leader decides.
          </p>
          <Link
            to="/clans/browse"
            className={`btn-ghost ${MORPH} flex w-full items-center justify-center gap-1.5`}
          >
            <Users className="h-3 w-3" />
            Browse Open Clans
          </Link>
        </Card>
      </div>

      {/* Inline discovery — Phase 3.5 wiring of GET /clans/discover. */}
      <DiscoverClansInline />
    </PageWrapper>
  );
}

// ── Leader Panel ──────────────────────────────────────────────────────────────

export function LeaderPanel() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Parameters<typeof ToastHost>[0]['toast']>(null);
  const { data: me, isLoading: meLoading } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: clan, isLoading: clanLoading } = useQuery({
    queryKey: ['clan', me?.clanName],
    queryFn: () => api.clan(me!.clanName!),
    enabled: !!me?.clanName,
  });

  // Delegation-visibility audit (T4, 2026-07-21) — "am I an active captain,
  // and of which team". Deliberately NOT read off clan.activeDelegations:
  // that field is only populated by GET /clan/:name for callers who already
  // pass clan.manage_roles (owner/co-admin/platform admin) — a plain-member
  // captain's own grant never rides through it. GET /clans/:name/my-grant
  // (api.myClanGrant) is the one endpoint that answers this for a non-owner
  // caller — same call JoinCompScreen already uses for the identical check.
  const { data: myGrant } = useQuery({
    queryKey: ['myClanGrant', me?.clanName],
    queryFn: () => api.myClanGrant(me!.clanName!),
    enabled: !!me?.clanName,
  });

  // Hooks must run unconditionally on every render — keep these ABOVE the early
  // returns below, or React throws "rendered more hooks than during the previous render".
  const effectiveRole = useEffectiveRole(me?.role);
  // View-as-aware copy of `me` — every PER-CLAN identity check below reads
  // THIS, not the raw /me row. The role leg (effectiveRole) was already
  // view-as-aware; the identity leg was not, and this page's most powerful
  // signal is an identity comparison: `clan.leaderId === discordId`. Reading
  // the raw id meant a founder who genuinely owns the clan they are inspecting
  // kept full ownership through a preview — the owner-only Captains panel, the
  // owner-scoped seat controls, and the per-row delegate/assign-team gates all
  // stayed live, none of which pass through the viewingAsLowerRole guard below.
  // lib/viewAs.ts is explicit that the id IS a grant (see
  // VIEW_AS_SIM_DISCORD_ID). Identity no-op whenever no view-as is active.
  const effectiveMe = useEffectiveMe(me);
  // Hook, so it must stay above the early returns below with the others.
  const { viewAs } = useViewAs();

  if (meLoading || clanLoading) return <PageSpinner />;

  if (!me || me.role === 'public') {
    return (
      <Card className="text-center">
        <p className="text-ink-secondary">Login required.</p>
      </Card>
    );
  }

  if (!clan) {
    return <NoClanLanding />;
  }

  // Array (not a single number) so this stays correct if a future multi-grant
  // lookup ever replaces the single-grant my-grant endpoint — today it only
  // ever holds 0 or 1 entries.
  //
  // The CAPTAIN leg of "me" — the third grant this page reads, after the role
  // and the discordId. `myGrant` is GET /clans/:name/my-grant, answered for
  // the REAL caller and not role-filtered under a preview, so it still
  // describes the founder rather than the persona. Dropped while previewing,
  // exactly as PublicMatchCard.tsx does for the match-card equivalent
  // (`viewerCaptainOf`). This one matters even more than the role leg it sits
  // beside: canManageTeamScoped is `canManage || isCaptainOfTeam(...)`, so a
  // live captain grant granted seat + roster management back even though the
  // role-based canManage was already correctly false under the preview —
  // GhostSeatManager, ClanTeamsBoard and canAssignTeam all read this.
  // Identity no-op whenever no view-as is active, so a real captain keeps
  // every own-team control they have today.
  const viewerCaptainTeams = !viewAs && myGrant?.teamNo != null ? [myGrant.teamNo] : [];

  const pendingSeats = clan.seats.filter(s => s.status === 'PENDING_CLAIM');
  const ghostSeats = clan.seats.filter(s => s.status === 'GHOST');

  const refreshClan = () => queryClient.invalidateQueries({ queryKey: ['clan', me?.clanName] });

  // Per-clan edit/manage gate (mirrors the backend scopeAuthz, which only lets
  // THIS clan's owner/leader manage it — never a global platform 'leader' role).
  // The user may edit/manage when they are:
  //   • the clan OWNER (clan.leaderId === their discordId), OR
  //   • a LEADER of THIS clan (their membership row's role/scopedRole is a
  //     leader/owner role — being a leader elsewhere does not count), OR
  //   • a platform admin/founder (canWrite).
  // Honour the founder "view as" toggle: useEffectiveRole returns the
  // impersonated role, so viewing-as-member downgrades canWrite to false AND
  // (because a founder rarely owns/leads the clan they're inspecting) hides the
  // leader controls — letting member flows be tested. When a founder IS the
  // owner/leader of the clan, viewing-as-member still hides the controls because
  // we gate the per-clan signals behind the effective role not being a downgrade.
  // (effectiveRole is computed above, before the early returns, per React hooks rules.)
  const viewingAsLowerRole = effectiveRole !== me.role; // founder impersonating a lesser role
  // The identity this page renders AS, past the early returns. `me` is proven
  // non-null above and useEffectiveMe never turns a real `me` into null, so the
  // fallback is unreachable — it exists only to keep the children's `me: Me`
  // contracts exact rather than widening them to accept null.
  const viewerMe: Me = effectiveMe ?? me;
  const myMembership = clan.members.find(m => m.discordId === viewerMe.discordId);
  const isClanOwner = clan.leaderId === viewerMe.discordId;
  const isClanLeader =
    myMembership?.scopedRole === 'CLAN_OWNER' ||
    myMembership?.scopedRole === 'CLAN_LEADER' ||
    myMembership?.role === 'LEADER';
  const hasPerClanLeadership = !viewingAsLowerRole && (isClanOwner || isClanLeader);

  const canEditBranding = hasPerClanLeadership || canWrite(effectiveRole);
  const canManageMembers = hasPerClanLeadership || canWrite(effectiveRole);
  // Promote/demote between member and leader = backend clan.manage_roles, which
  // is CLAN_OWNER-only (a co-leader can't mint another leader). So gate role
  // changes on per-clan ownership or a platform admin, honouring view-as.
  const canManageRoles = (!viewingAsLowerRole && isClanOwner) || canWrite(effectiveRole);

  // Doorway into JoinCompScreen's self-serve register-my-clan flow (closing
  // the "registration hole" — leaders had no in-portal way to reach it,
  // 2026-07-17). Gated on the PLATFORM role, not per-clan leadership or view-
  // as: registerMyClanInComp is leader-gated server-side regardless of which
  // clan a founder/admin happens to be inspecting, so this deliberately does
  // NOT honour view-as-downgrade the way the edit controls above do.
  const canEnterComp = canRegisterClan(me.role, me.clanName);

  return (
    <PageWrapper maxWidth="4xl" spacing="none">
      <div className={SETTLE} style={settleDelay(0)}>
      <ClanBrandingHero
        clanName={clan.name}
        description={clan.description}
        editable={canEditBranding}
        className="mb-6"
      />
      </div>

      {canEnterComp && (
        <div className={SETTLE} style={settleDelay(1)}>
          <Card className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-forge-400" />
              <div>
                <h2 className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
                  Ready to compete?
                </h2>
                <p className="text-xs lg:text-sm text-ink-meta">Enter {clan.name} into an open competition.</p>
              </div>
            </div>
            <Link to="/gate/join-comp" className={`btn-primary ${MORPH} inline-flex items-center gap-1.5`}>
              <Trophy className="h-3 w-3" strokeWidth={2.5} /> Enter a competition
            </Link>
          </Card>
        </div>
      )}

      <div className={SETTLE} style={settleDelay(2)}>
        <MyClanIntro hasClan={true} className="mb-6" />
      </div>

      {/* Applications — owner/co-admin only (Lisa, 2026-07-19): deciding
          membership applications is a clan-wide action the backend gates to
          real leadership (the delegation overlay never affirms it); this
          panel used to render for EVERY clan member, whose Approve/Decline
          clicks silently 403'd. Same gate the branding editor already uses. */}
      {canManageMembers && (
      <div className={SETTLE} style={settleDelay(3)}>
      <ClanApplicationsPanel
        clanName={clan.name}
        publicApplicationsOpen={Boolean(clan.publicApplicationsOpen)}
        className="mb-6"
      />
      </div>
      )}

      {/* Add Members — invite from Discord + reserved-seat pool */}
      <div className={SETTLE} style={settleDelay(4)}>
      <GhostSeatManager clanName={clan.name} seats={clan.seats} className="mb-6" me={viewerMe} canManage={canManageMembers} viewerCaptainTeams={viewerCaptainTeams} isClanOwner={isClanOwner} onToast={setToast} />
      </div>

      {/* Squad Board (DELEGATION_UI_ENABLED=1) replaces Captains card + Full Roster.
          Falls back to the original CaptainsPanel-only view when the flag is off. */}
      {DELEGATION_UI_ENABLED ? (
        <div className={SETTLE} style={settleDelay(5)}>
          <Card className="mb-6">
            <ClanTeamsBoard
              clan={clan}
              me={viewerMe}
              canManage={isClanOwner || canManageRoles}
              canManageRoles={canManageRoles}
              isClanOwner={isClanOwner}
              viewerCaptainTeams={viewerCaptainTeams}
              onToast={setToast}
            />
          </Card>
        </div>
      ) : (
        isClanOwner && (
          <div className={SETTLE} style={settleDelay(5)}>
            <CaptainsPanel clan={clan} />
          </div>
        )
      )}

      {/* Actions */}
      <div className={SETTLE} style={settleDelay(6)}>
      <ScheduleMatchForm clanName={clan.name} />
      </div>

      {/* Pending seat claims */}
      {pendingSeats.length > 0 && (
        <Card className="mb-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            <Gamepad2 className="h-4 w-4 text-forge-400" /> Pending Seat Claims ({pendingSeats.length})
          </h2>
          <p className="mb-4 text-xs lg:text-sm text-ink-meta">
            Approve or deny via the DM buttons the bot sent you.
          </p>
          <div className="space-y-2">
            {pendingSeats.map(seat => (
              <div
                key={seat.gamertag}
                className="flex items-center justify-between rounded-lg bg-white/[0.04] px-4 py-3"
              >
                <div>
                  <p className="text-sm lg:text-base font-medium text-ink-primary">{seat.gamertag}</p>
                  {seat.pendingClaimant && (
                    <p className="text-xs lg:text-sm text-ink-secondary">
                      Claimed by{' '}
                      <span className="text-ink-body">{seat.pendingClaimantName ?? 'a member'}</span>
                    </p>
                  )}
                </div>
                <StatusBadge status="PENDING_CLAIM" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Ghost seats */}
      {ghostSeats.length > 0 && (
        <Card className="mb-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            <Gamepad2 className="h-4 w-4 text-forge-400" /> Unfilled Seats ({ghostSeats.length})
          </h2>
          {/* The "/marshal_clan_join ..." command hint that used to sit here
              was removed (Lisa, 2026-07-19) — players claim seats through the
              portal/bot flows; surfacing a raw slash command read as clutter. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ghostSeats.map(seat => (
              <div
                key={seat.gamertag}
                className="rounded-lg bg-white/[0.04] px-3 py-2 text-sm lg:text-base text-ink-secondary"
              >
                {seat.gamertag}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Full roster — hidden when Squad Board is active (it subsumes this view) */}
      {!DELEGATION_UI_ENABLED && <Card className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
            <Users className="h-4 w-4 text-forge-400" /> Full Roster
          </h2>
          <button
            onClick={refreshClan}
            className="text-xs text-ink-meta hover:text-ink-secondary"
          >
            Refresh
          </button>
        </div>
        {clan.members.length === 0 ? (
          <p className="text-sm lg:text-base text-ink-meta">No members yet. Invite someone above.</p>
        ) : (
          <div className="space-y-2">
            {clan.members.map(m => (
              <ClanMemberRow
                key={m.discordId}
                member={m}
                clanName={clan.name}
                canManage={canManageTeamScoped(canManageMembers, viewerCaptainTeams, m.teamNo) && m.discordId !== viewerMe.discordId}
                canPromoteToLeader={canManageRoles && m.discordId !== viewerMe.discordId}
                canDelegate={DELEGATION_UI_ENABLED && isClanOwner && m.discordId !== viewerMe.discordId}
                // Own-team seat/bench assignment (2026-07-21): additive over
                // the existing isClanOwner/canManageRoles gate — a captain of
                // ANY team may also open the sheet (viewerCaptainTeams.length
                // > 0), scoped once open by canManageTeamsFully/
                // viewerCaptainTeams below to just their own team.
                canAssignTeam={DELEGATION_UI_ENABLED && (isClanOwner || canManageRoles || viewerCaptainTeams.length > 0) && m.discordId !== viewerMe.discordId}
                canManageTeamsFully={isClanOwner || canManageRoles}
                viewerCaptainTeams={viewerCaptainTeams}
                onToast={setToast}
              />
            ))}
          </div>
        )}
      </Card>}

      <ToastHost toast={toast} onDismiss={() => setToast(null)} />
    </PageWrapper>
  );
}
